import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';
import { useOrders } from '../../../contexts/OrdersContext';
import { getActiveRiders } from '../../../utils/riderStorage';
import type { AdminOrder } from '../../../mocks/adminOrders';
import { useToast } from '../../../contexts/ToastContext';

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

const formatPrice = (p: number) => `GYD $${p.toLocaleString()}`;

const paymentIcons: Record<string, string> = {
  'Credit / Debit Card': 'ri-bank-card-line',
  'Cash on Delivery': 'ri-money-dollar-circle-line',
};

export default function AdminPaymentsPage() {
  const toast = useToast();
  const { pendingPayments, confirmWithRider, updateStatus } = useOrders();
  const orders = pendingPayments;
  const [confirming, setConfirming] = useState<string | null>(null);
  const [riderSelections, setRiderSelections] = useState<Record<string, string>>({});
  const [riderErrors, setRiderErrors] = useState<Record<string, boolean>>({});
  const [availableRiders, setAvailableRiders] = useState<string[]>([]);

  useEffect(() => {
    setAvailableRiders(getActiveRiders().map((r) => r.name));
    const interval = setInterval(() => {
      setAvailableRiders(getActiveRiders().map((r) => r.name));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const totalAmount = orders.reduce((sum, o) => sum + o.total, 0);

  const handleConfirm = async (order: AdminOrder) => {
    const rider = riderSelections[order.id];
    if (!rider) {
      setRiderErrors((prev) => ({ ...prev, [order.id]: true }));
      return;
    }
    setRiderErrors((prev) => ({ ...prev, [order.id]: false }));
    setConfirming(order.id);
    await new Promise((r) => setTimeout(r, 600));
    confirmWithRider(order.id, rider);
    setConfirming(null);
    toast.success('Order confirmed!', `${order.id} assigned to ${rider}.`);
  };

  const handleReject = (order: AdminOrder) => {
    updateStatus(order.id, 'cancelled');
    toast.error('Payment rejected', `Order ${order.id} has been cancelled.`);
  };

  const setRider = (orderId: string, rider: string) => {
    setRiderSelections((prev) => ({ ...prev, [orderId]: rider }));
    setRiderErrors((prev) => ({ ...prev, [orderId]: false }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Payment Queue</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            Confirm payments and assign a rider before sending to expedition
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
              <i className="ri-time-line text-amber-500 text-lg" />
            </div>
            <div>
              <p className="text-xs text-stone-400 font-medium">Awaiting Confirmation</p>
              <p className="text-2xl font-bold text-stone-900">{orders.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
              <i className="ri-money-dollar-circle-line text-orange-500 text-lg" />
            </div>
            <div>
              <p className="text-xs text-stone-400 font-medium">Total Pending</p>
              <p className="text-2xl font-bold text-stone-900">GYD ${totalAmount.toLocaleString()}</p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center gap-3">
            <i className="ri-information-line text-amber-500 text-lg" />
            <p className="text-xs text-amber-700 leading-relaxed">
              Assign a rider before confirming. The rider will be notified for expedition.
            </p>
          </div>
        </div>

        {/* Empty state */}
        {orders.length === 0 && (
          <div className="bg-white rounded-xl border border-stone-200 py-20 flex flex-col items-center justify-center gap-4">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-green-500 text-2xl" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-stone-600">All caught up!</p>
              <p className="text-xs text-stone-400 mt-1">No payments waiting for confirmation.</p>
            </div>
          </div>
        )}

        {/* Orders */}
        {orders.length > 0 && (
          <div className="space-y-3">
            {orders.map((order) => {
              const selectedRider = riderSelections[order.id] ?? '';
              const hasError = riderErrors[order.id];
              const isConfirming = confirming === order.id;

              return (
                <div
                  key={order.id}
                  className={`bg-white rounded-xl border overflow-hidden transition ${
                    hasError ? 'border-red-300' : 'border-stone-200'
                  }`}
                >
                  {/* Main info row */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      {/* Left: customer */}
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-white text-sm font-bold">
                            {order.customer.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-stone-900">{order.customer}</span>
                            <span className="text-xs font-mono text-stone-400">{order.id}</span>
                            <span className="text-xs text-stone-400">{formatRelative(order.createdAt)}</span>
                          </div>
                          <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-1">
                            <i className="ri-map-pin-line text-stone-400" />
                            {order.address}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {order.items.map((item) => (
                              <span
                                key={item.name}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 rounded-full text-xs text-stone-600"
                              >
                                {item.name} ×{item.qty}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right: payment info */}
                      <div className="text-right shrink-0">
                        <p className="text-xl font-bold text-stone-900">{formatPrice(order.total)}</p>
                        <div className="flex items-center gap-1.5 justify-end mt-0.5">
                          <i className={`${paymentIcons[order.paymentMethod] ?? 'ri-bank-card-line'} text-stone-400 text-xs`} />
                          <span className="text-xs text-stone-500">{order.paymentMethod}</span>
                        </div>
                        <p className="text-[10px] text-stone-400 mt-0.5">{formatDate(order.createdAt)}</p>
                        {/* Change badge */}
                        {order.paymentMethod === 'Cash on Delivery' &&
                          order.changeAmount !== undefined &&
                          order.changeAmount >= 0 && (
                            <div className="mt-1.5 inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
                              <i className="ri-coins-line text-amber-500 text-xs" />
                              <span className="text-xs font-bold text-amber-700">
                                Troco: {formatPrice(order.changeAmount)}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Rider + actions row */}
                  <div className={`px-5 py-3.5 border-t flex items-center gap-3 flex-wrap ${hasError ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-100'}`}>
                    {/* Rider selector */}
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${selectedRider ? 'bg-green-100' : hasError ? 'bg-red-100' : 'bg-stone-200'}`}>
                        <i className={`ri-motorbike-line text-sm ${selectedRider ? 'text-green-600' : hasError ? 'text-red-500' : 'text-stone-500'}`} />
                      </div>
                      <div className="flex-1">
                        <label className={`block text-[10px] font-semibold uppercase tracking-wide mb-1 ${hasError ? 'text-red-600' : 'text-stone-400'}`}>
                          {hasError ? 'Select a rider to continue' : 'Assign Rider'}
                        </label>
                        <select
                          value={selectedRider}
                          onChange={(e) => setRider(order.id, e.target.value)}
                          className={`w-full text-sm font-medium rounded-lg px-3 py-1.5 border focus:outline-none focus:ring-2 transition cursor-pointer bg-white ${
                            hasError
                              ? 'border-red-300 text-red-600 focus:ring-red-200'
                              : selectedRider
                              ? 'border-green-300 text-stone-800 focus:ring-green-200'
                              : 'border-stone-200 text-stone-400 focus:ring-orange-100 focus:border-orange-300'
                          }`}
                        >
                          <option value="">— Choose a rider —</option>
                          {availableRiders.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                          {availableRiders.length === 0 && (
                            <option disabled>No riders on duty</option>
                          )}
                        </select>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleReject(order)}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-close-circle-line text-sm" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleConfirm(order)}
                        disabled={isConfirming}
                        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg transition cursor-pointer whitespace-nowrap ${
                          !selectedRider
                            ? 'bg-stone-300 hover:bg-stone-400'
                            : 'bg-green-500 hover:bg-green-600'
                        } disabled:opacity-60`}
                      >
                        {isConfirming ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-sm" />
                            Confirming...
                          </>
                        ) : (
                          <>
                            <i className="ri-checkbox-circle-line text-sm" />
                            {selectedRider ? `Confirm & Send to ${selectedRider.split(' ')[0]}` : 'Confirm Payment'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Note */}
                  {order.note && (
                    <div className="px-5 py-3 border-t border-stone-100 flex items-start gap-2">
                      <i className="ri-sticky-note-line text-stone-400 text-sm mt-0.5" />
                      <p className="text-xs text-stone-500">{order.note}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
